/**
 * Churn Prevention Calculator
 * 
 * Provides utilities to calculate when contracts become "free" to switch
 * without penalties based on supplier-specific churn periods.
 * 
 * Formula: Free Date = Signing Date + 15 days (processing) + 45 days (switch) + Supplier Churn Months
 * 
 * IMPORTANT: Uses signing_date (when contract was signed) if available, falls back to created_at (when entered in CRM).
 * This allows for retro-dating contracts that were signed before being entered into the system.
 * 
 * Note: Uses 'clawback_months' from supplier.custom_data (aligned with Suppliers.jsx UI)
 */

// Constants
const PROCESSING_DAYS = 15; // Days for internal processing and paperwork
const SWITCH_DAYS = 45;     // Average days for supplier switch process

/**
 * Get the base date for churn calculations
 * Prefers signing_date (actual contract signature) over created_at (CRM entry date)
 * @param {Object} contract - Contract/item object
 * @returns {string|null} ISO date string to use as base
 */
export const getContractBaseDate = (contract) => {
    // Prefer signing_date direct column (new schema), then custom_data fallback, then created_at
    return contract?.signing_date ||
        contract?.custom_data?.signing_date ||
        contract?.created_at ||
        null;
};

/**
 * Calculate the date when a contract becomes free to switch
 * @param {string|Date} baseDate - Contract signing date (or creation date as fallback)
 * @param {number} clawbackMonths - Supplier-specific churn period in months (from custom_data.clawback_months)
 * @returns {Date} The date when contract becomes free
 */
export const calculateFreeDate = (baseDate, clawbackMonths = 0) => {
    if (!baseDate) return null;

    const created = new Date(baseDate);
    const freeDate = new Date(created);

    // Add processing time
    freeDate.setDate(freeDate.getDate() + PROCESSING_DAYS);

    // Add switch time
    freeDate.setDate(freeDate.getDate() + SWITCH_DAYS);

    // Add supplier churn period ONLY if > 0
    // If clawback is 0 or null, contract is free after processing + switch
    if (clawbackMonths && clawbackMonths > 0) {
        freeDate.setMonth(freeDate.getMonth() + parseInt(clawbackMonths));
    }

    return freeDate;
};

/**
 * Calculates churn status and days remaining
 * 
 * Status definitions:
 * - free: Contract is ready for switching (green)
 * - warning: Less than 30 days remaining (orange)
 * - locked: More than 30 days remaining (red)
 * 
 * @param {Date|null} freeDate - Date when contract becomes free
 * @returns {Object} Status object with status, daysRemaining, label, color, icon
 */
export function getChurnStatus(freeDate) {
    // Contracts with no churn period or invalid date are always free
    if (!freeDate) {
        return {
            status: 'free',
            daysRemaining: 0,
            label: 'Libero',
            color: 'green',
            icon: '●',
            notify: false
        };
    }

    const now = new Date();
    const diffTime = freeDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Contract is free
    if (diffDays <= 0) {
        return {
            status: 'free',
            daysRemaining: 0,
            label: 'Libero',
            color: 'green',
            icon: '●',
            notify: true // Should send notification
        };
    }

    // Warning: less than 30 days
    if (diffDays <= 30) {
        return {
            status: 'warning',
            daysRemaining: diffDays,
            label: `${diffDays} ${diffDays === 1 ? 'giorno rimanente' : 'giorni rimanenti'}`,
            color: 'orange',
            icon: '●',
            notify: false
        };
    }

    // Locked: more than 30 days
    return {
        status: 'locked',
        daysRemaining: diffDays,
        label: `${diffDays} giorni rimanenti`,
        color: 'red',
        icon: '●',
        notify: false
    };
}

/**
 * Formats a date in Italian locale
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatFreeDate(date) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return 'N/D';
    }

    return date.toLocaleDateString('it-IT', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });
}

/**
 * Check if notification should be sent for a contract
 * @param {Object} contract - Contract object
 * @param {Object} supplier - Supplier object with clawback_months
 * @param {Array} sentNotifications - Previously sent notifications
 */
export const shouldNotify = (contract, supplier, sentNotifications = []) => {
    const clawbackMonths = supplier?.custom_data?.clawback_months || 0;
    const freeDate = calculateFreeDate(contract.created_at, clawbackMonths);
    const status = getChurnStatus(freeDate);

    // Only notify if free and not already notified
    if (status.status === 'free' && status.notify) {
        const alreadyNotified = sentNotifications.some(
            n => n.item_id === contract.id && n.notification_type === 'contract_free'
        );
        return !alreadyNotified;
    }

    return false;
}

/**
 * Gets color classes for Tailwind based on status
 * @param {string} color - Color name (green, orange, red)
 * @param {string} type - Type of style (bg, text, border)
 * @returns {string} Tailwind classes
 */
export function getChurnColorClasses(color, type = 'bg') {
    const classes = {
        green: {
            bg: 'bg-green-500',
            text: 'text-green-600 dark:text-green-400',
            border: 'border-green-200 dark:border-green-800',
            bgLight: 'bg-green-100 dark:bg-green-950/30'
        },
        orange: {
            bg: 'bg-orange-500',
            text: 'text-orange-600 dark:text-orange-400',
            border: 'border-orange-200 dark:border-orange-800',
            bgLight: 'bg-orange-100 dark:bg-orange-950/30'
        },
        red: {
            bg: 'bg-red-500',
            text: 'text-red-600 dark:text-red-400',
            border: 'border-red-200 dark:border-red-800',
            bgLight: 'bg-red-100 dark:bg-red-950/30'
        }
    };

    return classes[color]?.[type] || '';
}
